import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Plus, LinkSimple, PencilSimple, UploadSimple, Code, Warning } from "@phosphor-icons/react";
import { useGeo, AreaPicker } from "./RadarGeo";

export const RadarImportDialog = ({ onImported }) => {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState("url");
    // When URL import fails because the host is unreachable / blocked, we
    // pass the URL along to the HTML-paste tab so the user just has to paste
    // the page source.
    const [prefillUrl, setPrefillUrl] = useState("");

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    data-testid="radar-import-open-btn"
                    className="h-11 px-5 flex items-center gap-2 border border-gray-300 bg-white text-sm font-semibold hover:border-black transition-colors"
                >
                    <Plus size={16} weight="bold" />
                    Importa asta reale
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="radar-import-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display-black text-2xl">
                        Importa un&apos;asta reale
                    </DialogTitle>
                    <DialogDescription className="text-sm text-gray-500">
                        Aggiungi un&apos;asta reale tramite URL del portale pubblico, incollando il sorgente HTML, o inserimento manuale dei dati.
                    </DialogDescription>
                </DialogHeader>
                <Tabs value={tab} onValueChange={setTab} className="mt-2">
                    <TabsList className="grid grid-cols-3 w-full">
                        <TabsTrigger value="url" data-testid="tab-url-import">
                            <LinkSimple size={15} weight="bold" className="mr-2" />
                            Da URL
                        </TabsTrigger>
                        <TabsTrigger value="html" data-testid="tab-html-import">
                            <Code size={15} weight="bold" className="mr-2" />
                            Da HTML
                        </TabsTrigger>
                        <TabsTrigger value="manual" data-testid="tab-manual-import">
                            <PencilSimple size={15} weight="bold" className="mr-2" />
                            Manuale
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="url">
                        <UrlImportForm
                            onDone={(radarId) => { setOpen(false); onImported?.(radarId); }}
                            onUnreachable={(url) => { setPrefillUrl(url); setTab("html"); }}
                        />
                    </TabsContent>
                    <TabsContent value="html">
                        <HtmlImportForm
                            initialUrl={prefillUrl}
                            onDone={(radarId) => { setOpen(false); onImported?.(radarId); }}
                        />
                    </TabsContent>
                    <TabsContent value="manual">
                        <ManualImportForm
                            onDone={(radarId) => { setOpen(false); onImported?.(radarId); }}
                        />
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};

const UrlImportForm = ({ onDone, onUnreachable }) => {
    const { regions, provincesOf } = useGeo();
    const [url, setUrl] = useState("");
    const [geoVal, setGeoVal] = useState({ region: "", province: "", comune: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!url.trim()) return;
        setLoading(true);
        try {
            const payload = { url: url.trim() };
            if (geoVal.province) payload.province_hint = geoVal.province;
            const { data } = await api.post("/radar/url-import", payload);
            const docMsg = data.documents_found ? ` · ${data.documents_found} documenti` : "";
            if (data.status === "merged") {
                toast.success(`Asta già presente: aggiornata.${docMsg}`);
            } else {
                toast.success(
                    `Asta importata · score ${data.prescreening_score} (${data.prescreening_category})${docMsg}`
                );
            }
            onDone(data.radar_id);
        } catch (err) {
            const detail = err.response?.data?.detail;
            // Structured failure → if fallback is paste_html, switch tab
            if (detail && typeof detail === "object" && detail.fallback === "paste_html") {
                toast.error(detail.message || "Sito irraggiungibile. Usa la tab “Da HTML”.");
                onUnreachable?.(detail.url || url.trim());
            } else {
                toast.error(formatApiError(detail));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-4 pt-4" data-testid="url-import-form">
            <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-600">
                    URL dell&apos;asta (portale pubblico)
                </Label>
                <Input
                    data-testid="url-import-input"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://www.astegiudiziarie.it/vendita-asta-..."
                    className="mt-1.5"
                />
                <p className="text-xs text-gray-500 mt-1.5">
                    Il sistema scarica la pagina nel rispetto del robots.txt, estrae i dati e
                    calcola un pre-screening score. Non si aggirano CAPTCHA, login o paywall.
                    Se il sito blocca l&apos;accesso ai nostri server (es. tribunali regionali),
                    verrai indirizzato alla tab <b>“Da HTML”</b> per incollare il sorgente della pagina.
                </p>
            </div>
            <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-600 block mb-1.5">
                    Provincia (se non rilevabile dall&apos;URL)
                </Label>
                <AreaPicker
                    regions={regions}
                    provincesOf={provincesOf}
                    value={geoVal}
                    onChange={setGeoVal}
                    comune={false}
                    idPrefix="url-geo"
                />
            </div>
            <button
                type="submit"
                disabled={loading}
                data-testid="url-import-submit"
                className="h-11 px-5 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 transition-colors"
            >
                {loading ? "Importazione…" : "Importa e pre-screen"}
            </button>
        </form>
    );
};

const HtmlImportForm = ({ initialUrl = "", onDone }) => {
    const { regions, provincesOf } = useGeo();
    const [url, setUrl] = useState(initialUrl);
    const [html, setHtml] = useState("");
    const [geoVal, setGeoVal] = useState({ region: "", province: "", comune: "" });
    const [loading, setLoading] = useState(false);

    // Sync prefilled URL when the parent switches tab after an URL-import failure
    useEffect(() => {
        if (initialUrl && !url) setUrl(initialUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialUrl]);

    const submit = async (e) => {
        e.preventDefault();
        if (!url.trim() || !html.trim()) {
            toast.error("Inserisci URL e sorgente HTML della pagina.");
            return;
        }
        setLoading(true);
        try {
            const payload = { url: url.trim(), html };
            if (geoVal.province) payload.province_hint = geoVal.province;
            const { data } = await api.post("/radar/url-import-html", payload);
            const docMsg = data.documents_found ? ` · ${data.documents_found} documenti` : "";
            if (data.status === "merged") {
                toast.success(`Asta già presente: aggiornata.${docMsg}`);
            } else {
                toast.success(
                    `Asta importata da HTML · score ${data.prescreening_score} (${data.prescreening_category})${docMsg}`
                );
            }
            onDone(data.radar_id);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    const labelCls = "text-xs uppercase tracking-wider font-bold text-gray-600";

    return (
        <form onSubmit={submit} className="space-y-4 pt-4" data-testid="html-import-form">
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 text-amber-900 text-xs leading-relaxed">
                <Warning size={16} weight="bold" className="mt-0.5 shrink-0" />
                <div className="space-y-2">
                    <div>
                        <b>Quando usarlo:</b> se il sito blocca i nostri server (es. <code>tribunale.firenze.giustizia.it</code>) o restituisce una pagina vuota perché è un&apos;applicazione JavaScript (es. <code>pvp.giustizia.it</code>).
                    </div>
                    <div>
                        <b>Per siti statici</b> (Astalegale, Astegiudiziarie, Tribunali): tasto destro → <b>Visualizza sorgente pagina</b> (<code>Ctrl+U</code>) → <code>Ctrl+A</code> → <code>Ctrl+C</code> e incolla qui.
                    </div>
                    <div>
                        <b>Per siti SPA</b> (PVP, portali Angular/React): <code>F12</code> per aprire DevTools → tab <b>Elements/Elementi</b> → tasto destro sul tag <code>&lt;html&gt;</code> → <b>Copy outerHTML</b> → incolla qui.
                    </div>
                </div>
            </div>
            <div>
                <Label className={labelCls}>URL originale dell&apos;asta</Label>
                <Input
                    data-testid="html-import-url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://www.tribunale.firenze.giustizia.it/it/Aste/DettaglioImmobile/..."
                    className="mt-1.5"
                />
            </div>
            <div>
                <Label className={labelCls}>Sorgente HTML della pagina</Label>
                <Textarea
                    data-testid="html-import-textarea"
                    value={html}
                    onChange={(e) => setHtml(e.target.value)}
                    placeholder="<!DOCTYPE html>… (incolla qui il sorgente completo della pagina)"
                    className="mt-1.5 font-mono text-xs"
                    rows={10}
                />
                <p className="text-xs text-gray-500 mt-1.5">
                    Massimo 5MB. I PDF perizia/avviso vengono estratti automaticamente dai link presenti nell&apos;HTML.
                </p>
            </div>
            <div>
                <Label className={labelCls + " block mb-1.5"}>Provincia (opzionale)</Label>
                <AreaPicker
                    regions={regions}
                    provincesOf={provincesOf}
                    value={geoVal}
                    onChange={setGeoVal}
                    comune={false}
                    idPrefix="html-geo"
                />
            </div>
            <button
                type="submit"
                disabled={loading}
                data-testid="html-import-submit"
                className="h-11 px-5 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 transition-colors"
            >
                {loading ? "Parsing…" : "Importa da HTML"}
            </button>
        </form>
    );
};

const ManualImportForm = ({ onDone }) => {
    const { regions, provincesOf } = useGeo();
    const [form, setForm] = useState({
        title: "", source_url: "", province: "", municipality: "",
        address: "", base_price: "", minimum_bid: "", surface_sqm: "",
        rooms: "", property_type: "appartamento", court: "",
        procedure_number: "", auction_date: "", description: "",
    });
    const [geoVal, setGeoVal] = useState({ region: "", province: "", comune: "" });
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);

    const setGeo = (v) => {
        setGeoVal(v);
        setForm((f) => ({ ...f, province: v.province || "", municipality: v.comune || f.municipality }));
    };

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) {
            toast.error("Il titolo è obbligatorio");
            return;
        }
        setLoading(true);
        try {
            const payload = { ...form };
            ["base_price", "minimum_bid", "surface_sqm", "rooms"].forEach((k) => {
                payload[k] = payload[k] === "" ? null : Number(payload[k]);
            });
            Object.keys(payload).forEach((k) => {
                if (payload[k] === "") payload[k] = null;
            });
            const { data } = await api.post("/radar/manual", payload);
            if (file) {
                const fd = new FormData();
                fd.append("kind", "perizia");
                fd.append("file", file);
                await api.post(`/radar/manual/${data.id}/documents`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }
            toast.success(`Asta inserita · score ${data.prescreening_score}`);
            onDone(data.id);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    const fieldCls = "mt-1.5";
    const labelCls = "text-xs uppercase tracking-wider font-bold text-gray-600";

    return (
        <form onSubmit={submit} className="space-y-4 pt-4 max-h-[60vh] overflow-y-auto pr-1" data-testid="manual-import-form">
            <div>
                <Label className={labelCls}>Titolo *</Label>
                <Input data-testid="manual-title" value={form.title} onChange={set("title")} className={fieldCls} />
            </div>
            <div>
                <Label className={labelCls + " block mb-1.5"}>Area (regione · provincia · comune)</Label>
                <AreaPicker
                    regions={regions}
                    provincesOf={provincesOf}
                    value={geoVal}
                    onChange={setGeo}
                    idPrefix="manual-geo"
                />
                <Input
                    data-testid="manual-municipality"
                    value={form.municipality}
                    onChange={set("municipality")}
                    placeholder="Comune"
                    className="mt-2 max-w-xs"
                />
            </div>
            <div>
                <Label className={labelCls}>Indirizzo</Label>
                <Input data-testid="manual-address" value={form.address} onChange={set("address")} className={fieldCls} />
            </div>
            <div className="grid grid-cols-3 gap-3">
                <div>
                    <Label className={labelCls}>Prezzo base €</Label>
                    <Input type="number" data-testid="manual-base-price" value={form.base_price} onChange={set("base_price")} className={fieldCls} />
                </div>
                <div>
                    <Label className={labelCls}>Offerta min €</Label>
                    <Input type="number" data-testid="manual-min-bid" value={form.minimum_bid} onChange={set("minimum_bid")} className={fieldCls} />
                </div>
                <div>
                    <Label className={labelCls}>Superficie m²</Label>
                    <Input type="number" data-testid="manual-surface" value={form.surface_sqm} onChange={set("surface_sqm")} className={fieldCls} />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className={labelCls}>Tribunale</Label>
                    <Input data-testid="manual-court" value={form.court} onChange={set("court")} className={fieldCls} />
                </div>
                <div>
                    <Label className={labelCls}>N. procedura (RGE)</Label>
                    <Input data-testid="manual-procedure" value={form.procedure_number} onChange={set("procedure_number")} className={fieldCls} />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className={labelCls}>Data asta (ISO)</Label>
                    <Input data-testid="manual-date" value={form.auction_date} onChange={set("auction_date")} placeholder="2026-05-12T10:00:00" className={fieldCls} />
                </div>
                <div>
                    <Label className={labelCls}>URL fonte</Label>
                    <Input data-testid="manual-source-url" value={form.source_url} onChange={set("source_url")} className={fieldCls} />
                </div>
            </div>
            <div>
                <Label className={labelCls}>Descrizione</Label>
                <Textarea data-testid="manual-description" value={form.description} onChange={set("description")} className={fieldCls} rows={2} />
            </div>
            <div>
                <Label className={labelCls}>Perizia PDF (opzionale)</Label>
                <label className="mt-1.5 flex items-center gap-2 h-11 px-4 border border-dashed border-gray-300 cursor-pointer hover:border-black transition-colors text-sm text-gray-600">
                    <UploadSimple size={16} weight="bold" />
                    {file ? file.name : "Carica la perizia tecnica reale (PDF)"}
                    <input
                        type="file"
                        accept="application/pdf"
                        data-testid="manual-pdf-input"
                        className="hidden"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                </label>
            </div>
            <button
                type="submit"
                disabled={loading}
                data-testid="manual-import-submit"
                className="h-11 px-5 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 transition-colors"
            >
                {loading ? "Salvataggio…" : "Salva nel Radar"}
            </button>
        </form>
    );
};
